import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box
} from '@mui/material';
import { StagedUser } from '../services/HxcStagingService';
import { logger } from 'utils/logger';

const log = logger.getLogger('StagingUserFormModal');

interface StagingUserFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (user: Omit<StagedUser, 'id'>) => Promise<void>;
  user?: StagedUser | null;  // If provided, edit mode; otherwise create mode
  mode: 'create' | 'edit';
  allUsers: StagedUser[];  // All users for validation checks
}

const StagingUserFormModal: React.FC<StagingUserFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  user,
  mode,
  allUsers
}) => {
  const [formData, setFormData] = useState({
    accountName: '',
    sfdcAccountId: '',
    firstName: '',
    lastName: '',
    email: ''
  });
  
  const [originalData, setOriginalData] = useState({
    accountName: '',
    sfdcAccountId: '',
    firstName: '',
    lastName: '',
    email: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  
  // Strict email validation
  const isValidEmail = (email: string): boolean => {
    // Strict email regex that ensures TLD only contains letters
    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    
    if (!emailRegex.test(email)) {
      return false;
    }
    
    // Additional check: ensure no numbers after the last dot in domain
    const domainPart = email.split('@')[1];
    if (!domainPart) {
      return false;
    }
    
    const tld = domainPart.split('.').pop();
    if (!tld || /\d/.test(tld)) {
      // TLD contains numbers, invalid
      return false;
    }
    
    return true;
  };
  
  // Helper functions for table-style validation
  const checkDuplicateEmail = useCallback((email: string, currentUserId?: string): boolean => {
    return allUsers.some(u => 
      u.email.toLowerCase() === email.toLowerCase() && 
      u.id !== currentUserId
    );
  }, [allUsers]);
  
  const checkMalformedSfdcId = useCallback((sfdcId: string): boolean => {
    return sfdcId.length !== 15 || !/^[a-zA-Z0-9]+$/.test(sfdcId);
  }, []);
  
  const checkSfdcIdMismatch = useCallback((accountName: string, sfdcId: string, currentUserId?: string): boolean => {
    // Find first occurrence of this account name (excluding current user)
    const firstOccurrence = allUsers.find(u => 
      u.accountName.toLowerCase() === accountName.toLowerCase() && 
      u.id !== currentUserId
    );
    return firstOccurrence ? firstOccurrence.sfdcAccountId !== sfdcId : false;
  }, [allUsers]);
  
  const checkDuplicateSfdcId = useCallback((accountName: string, sfdcId: string, currentUserId?: string): boolean => {
    // Find first occurrence of this SFDC ID (by user order)
    const firstOccurrence = allUsers.find(u => u.sfdcAccountId === sfdcId);
    return firstOccurrence 
      ? firstOccurrence.accountName.toLowerCase() !== accountName.toLowerCase() && 
        firstOccurrence.id !== currentUserId
      : false;
  }, [allUsers]);
  
  // Get initial validation errors based on table checks
  const getInitialValidationErrors = useCallback((userData: typeof formData, userId?: string): { [key: string]: string } => {
    const initialErrors: { [key: string]: string } = {};
    
    // Check account name length
    if (userData.accountName.length > 80) {
      initialErrors.accountName = 'Must be 80 characters or less';
    }
    
    // Check first name
    if (!userData.firstName || userData.firstName.trim() === '') {
      initialErrors.firstName = 'Required';
    }
    
    // Check last name
    if (!userData.lastName || userData.lastName.trim() === '') {
      initialErrors.lastName = 'Required';
    }
    
    // Check email
    if (checkDuplicateEmail(userData.email, userId)) {
      initialErrors.email = 'Duplicate email';
    }
    
    // Check SFDC ID
    if (checkMalformedSfdcId(userData.sfdcAccountId)) {
      initialErrors.sfdcAccountId = 'Invalid SFDC ID';
    } else if (checkDuplicateSfdcId(userData.accountName, userData.sfdcAccountId, userId)) {
      initialErrors.sfdcAccountId = 'Duplicate SFDC ID';
    } else if (checkSfdcIdMismatch(userData.accountName, userData.sfdcAccountId, userId)) {
      initialErrors.sfdcAccountId = 'ID Mismatch';
    }
    
    return initialErrors;
  }, [checkDuplicateEmail, checkDuplicateSfdcId, checkSfdcIdMismatch, checkMalformedSfdcId]);
  
  // Initialize form data when user prop changes
  useEffect(() => {
    if (mode === 'edit' && user) {
      const data = {
        accountName: user.accountName,
        sfdcAccountId: user.sfdcAccountId,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email
      };
      setFormData(data);
      setOriginalData(data);
      
      // Set initial validation errors from table checks
      const initialErrors = getInitialValidationErrors(data, user.id);
      setErrors(initialErrors);
      
      // Mark fields with errors as touched so they show immediately
      const initialTouched: { [key: string]: boolean } = {};
      Object.keys(initialErrors).forEach(key => {
        initialTouched[key] = true;
      });
      setTouched(initialTouched);
    } else {
      // Reset for create mode
      const emptyData = {
        accountName: '',
        sfdcAccountId: '',
        firstName: '',
        lastName: '',
        email: ''
      };
      setFormData(emptyData);
      setOriginalData(emptyData);
      setErrors({});
      setTouched({});
    }
  }, [user, mode, open, getInitialValidationErrors]);
  
  // Handle field changes
  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    
    // Apply field-specific constraints
    if (field === 'accountName') {
      // Limit to 80 characters
      value = value.slice(0, 80);
    } else if (field === 'sfdcAccountId') {
      // Limit to 15 alphanumeric characters
      value = value.slice(0, 15).replace(/[^a-zA-Z0-9]/g, '');
    }
    
    setFormData({
      ...formData,
      [field]: value
    });
    
    // If field has been touched, validate it in real-time
    if (touched[field]) {
      validateField(field, value);
    }
  };
  
  // Validate a single field
  const validateField = (field: string, value: string) => {
    let error = '';
    
    switch (field) {
      case 'accountName':
        if (!value.trim()) {
          error = 'Account name is required';
        } else if (value.length > 80) {
          error = 'Account name must be 80 characters or less';
        }
        break;
      case 'sfdcAccountId':
        if (!value.trim()) {
          error = 'SFDC Account ID is required';
        } else if (checkMalformedSfdcId(value)) {
          error = 'Invalid SFDC ID';
        } else if (checkDuplicateSfdcId(formData.accountName, value, user?.id)) {
          error = 'Duplicate SFDC ID';
        } else if (checkSfdcIdMismatch(formData.accountName, value, user?.id)) {
          error = 'ID Mismatch';
        }
        break;
      case 'firstName':
        if (!value.trim()) {
          error = 'Required';
        }
        break;
      case 'lastName':
        if (!value.trim()) {
          error = 'Required';
        }
        break;
      case 'email':
        if (!value.trim()) {
          error = 'Email is required';
        } else if (!isValidEmail(value)) {
          error = 'Invalid email format';
        } else if (checkDuplicateEmail(value, user?.id)) {
          error = 'Duplicate email';
        }
        break;
    }
    
    setErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };
  
  // Handle field blur (when user leaves the field)
  const handleBlur = (field: string) => () => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
    validateField(field, formData[field as keyof typeof formData]);
  };
  
  // Validate form
  // Returns false only if there are EMAIL errors (allows saving with other field errors)
  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    
    // Validate all fields and show errors, but only block on email errors
    if (!formData.accountName.trim()) {
      newErrors.accountName = 'Account Name is required';
    } else if (formData.accountName.length > 80) {
      newErrors.accountName = 'Must be 80 characters or less';
    }
    
    if (!formData.sfdcAccountId.trim()) {
      newErrors.sfdcAccountId = 'SFDC Account ID is required';
    } else if (checkMalformedSfdcId(formData.sfdcAccountId)) {
      newErrors.sfdcAccountId = 'Invalid SFDC ID';
    } else if (checkDuplicateSfdcId(formData.accountName, formData.sfdcAccountId, user?.id)) {
      newErrors.sfdcAccountId = 'Duplicate SFDC ID';
    } else if (checkSfdcIdMismatch(formData.accountName, formData.sfdcAccountId, user?.id)) {
      newErrors.sfdcAccountId = 'ID Mismatch';
    }
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Required';
    }
    
    // Email validation - this is the only field that blocks saving
    let hasEmailError = false;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      hasEmailError = true;
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
      hasEmailError = true;
    } else if (checkDuplicateEmail(formData.email, user?.id)) {
      newErrors.email = 'Duplicate email';
      hasEmailError = true;
    }
    
    setErrors(newErrors);
    
    // Only block saving if there are EMAIL errors
    // Other field errors are displayed but don't prevent saving
    return !hasEmailError;
  };
  
  // Handle submit (Create or Update)
  const handleSubmit = async () => {
    if (!validate()) return;
    
    try {
      setSubmitting(true);
      await onSubmit(formData);
      onClose();
    } catch (error: any) {
      log.error(`Error ${mode === 'create' ? 'creating' : 'updating'} user:`, error);
      
      // Check for duplicate email error in various possible error formats
      const errorMessage = 
        error?.response?.data?.message || 
        error?.response?.message ||
        error?.data?.message ||
        error?.message || 
        '';
      
      log.log('Error message extracted:', errorMessage);
      
      // Check if error message indicates duplicate email
      if (errorMessage.toLowerCase().includes('already exists') && 
          errorMessage.toLowerCase().includes('email')) {
        // Set error on email field
        setErrors(prev => ({
          ...prev,
          email: 'A user is already staged with this email.'
        }));
        // Mark email as touched so error shows
        setTouched(prev => ({
          ...prev,
          email: true
        }));
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle Clear (Create mode) - clear all fields
  const handleClear = () => {
    setFormData({
      accountName: '',
      sfdcAccountId: '',
      firstName: '',
      lastName: '',
      email: ''
    });
    setErrors({});
    setTouched({});
  };
  
  // Handle Reset (Edit mode) - reset to original values
  const handleReset = () => {
    setFormData(originalData);
    setErrors({});
    setTouched({});
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {mode === 'create' ? 'Add New User' : 'Edit User'}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, pt: 2 }}>
          <TextField
            label="Account Name"
            value={formData.accountName}
            onChange={handleChange('accountName')}
            onBlur={handleBlur('accountName')}
            error={!!errors.accountName}
            helperText={errors.accountName || `${formData.accountName.length}/80 characters`}
            fullWidth
            required
            inputProps={{ maxLength: 80 }}
          />
          
          <TextField
            label="SFDC Account ID"
            value={formData.sfdcAccountId}
            onChange={handleChange('sfdcAccountId')}
            onBlur={handleBlur('sfdcAccountId')}
            error={!!errors.sfdcAccountId}
            helperText={errors.sfdcAccountId || `${formData.sfdcAccountId.length}/15 characters`}
            fullWidth
            required
            inputProps={{ maxLength: 15 }}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, pt: 2 }}>
          
          <TextField
            label="First Name"
            value={formData.firstName}
            onChange={handleChange('firstName')}
            onBlur={handleBlur('firstName')}
            error={!!errors.firstName}
            helperText={errors.firstName}
            fullWidth
            required
          />
          
          <TextField
            label="Last Name"
            value={formData.lastName}
            onChange={handleChange('lastName')}
            onBlur={handleBlur('lastName')}
            error={!!errors.lastName}
            helperText={errors.lastName}
            fullWidth
            required
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, pt: 2 }}>
          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            onBlur={handleBlur('email')}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
            required
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        
        {mode === 'create' ? (
          <>
            <Button onClick={handleClear} disabled={submitting}>
              Clear
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={submitting}
            >
              Create
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleReset} disabled={submitting}>
              Reset
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={submitting}
            >
              Update
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StagingUserFormModal;
